// src/services/logoService.js
import api from '@/core/api/api';
import { getHttpStatusFromApiError } from '@/utils/errorHandler';

export const logoService = {
  /**
   * Intelligent logo extraction/retrieval
   * If tempLocation is provided, retrieves from there. Otherwise extracts from website.
   * @param {string} websiteUrl - The website URL to extract logo from
   * @param {string} tempLocation - Optional S3 temp location to retrieve existing logo
   * @returns {Promise} API response with logo data or error
   */
  async getOrExtractLogo(websiteUrl, tempLocation = null) {
    try {
      const payload = {
        website_url: websiteUrl,
        entity_type: 'firm'
      };

      // If we have temp location, ask backend to retrieve from there
      if (tempLocation) {
        payload.temp_location = tempLocation;
        payload.action = 'retrieve';
        console.log('🔗 [LOGO] Retrieving logo from temp location:', tempLocation);
      } else {
        payload.action = 'extract';
        console.log('🔗 [LOGO] Extracting new logo from website:', websiteUrl);
      }

      const response = await api.post('/manage_logos', payload);
      
      if (response.data.from_cache) {
        console.log('🔗 [LOGO] ✅ Retrieved existing logo from cache');
      } else {
        console.log('🔗 [LOGO] ✅ Extracted new logo from website');
      }
      
      return response.data;
    } catch (error) {
      console.error('🔗 [LOGO] ❌ Logo operation failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to get or extract logo'
      };
    }
  },

  /**
   * Extract logo from website using backend services (original method - kept for compatibility)
   * @param {string} websiteUrl - The website URL to extract logo from
   * @returns {Promise} API response with logo data or error
   */
  async extractFromWebsite(websiteUrl) {
    try {
      const response = await api.post('/manage_logos', {
        website_url: websiteUrl,
        entity_type: 'firm'
      });
      
      return response.data;
    } catch (error) {
      console.error('Logo extraction failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to extract logo from website'
      };
    }
  },

  /**
   * Upload manual logo file
   * @param {string} imageBase64 - Base64 encoded image data (with data:image/... prefix)
   * @param {string} contentType - MIME type of the image
   * @returns {Promise} API response with uploaded logo data
   */
  async uploadLogo(imageBase64, contentType) {
    try {
      const response = await api.post('/manage_logos', {
        image: imageBase64,
        content_type: contentType,
        entity_type: 'firm'
      });
      
      return response.data;
    } catch (error) {
      console.error('Logo upload failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to upload logo'
      };
    }
  },

  /**
   * Move temporary logo to permanent location after registration
   * @param {string} tempLocation - S3 location of temporary logo
   * @param {string} entityType - The entity type (firm, team_member, etc.)
   * @param {string} entityId - The entity ID
   * @returns {Promise} API response confirming logo move
   */
  async moveTempToPermanent(tempLocation, entityType, entityId) {
    try {
      const response = await api.patch('/manage_logos', {
        temp_logo_location: tempLocation,
        entity_type: entityType,
        entity_id: entityId
      });
      
      return response.data;
    } catch (error) {
      console.error('Logo move failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to move logo to permanent location'
      };
    }
  },

  /**
   * Retrieve existing logo for an entity
   * @param {string} entityType - Type of entity ('firm', 'user', 'institution')
   * @param {string} entityId - ID of the entity
   * @returns {Promise} API response with logo data
   */
  async getLogo(entityType, entityId) {
    try {
      const response = await api.get(`/manage_logos?entity_type=${entityType}&entity_id=${entityId}`);
      return response.data;
    } catch (error) {
      // 404 = no logo uploaded yet — not an error, just show placeholder.
      // Interceptors reject with formatApiError(); status is on statusCode, not response.
      if (getHttpStatusFromApiError(error) === 404) {
        return { status: 'not_found', image: null };
      }
      console.error('Logo retrieval failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to retrieve logo'
      };
    }
  },

  /**
   * Delete logo for an entity
   * @param {string} entityType - Type of entity ('firm', 'user', 'institution')  
   * @param {string} entityId - ID of the entity
   * @returns {Promise} API response confirming deletion
   */
  async deleteLogo(entityType, entityId) {
    try {
      const response = await api.delete(`/manage_logos?entity_type=${entityType}&entity_id=${entityId}`);
      
      return response.data;
    } catch (error) {
      console.error('Logo deletion failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to delete logo'
      };
    }
  },

  /**
   * Extract logo from website for existing entity (direct to permanent location)
   * @param {string} websiteUrl - Website URL to extract logo from
   * @param {string} entityType - Type of entity ('firm', 'user', 'institution')
   * @param {string} entityId - ID of the entity
   * @returns {Promise} API response with logo data
   */
  async extractLogoForEntity(websiteUrl, entityType, entityId) {
    try {
      const response = await api.post('/manage_logos', {
        website_url: websiteUrl,
        entity_type: entityType,
        entity_id: entityId
      });
      
      return response.data;
    } catch (error) {
      console.error('Logo extraction for entity failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to extract logo'
      };
    }
  },

  /**
   * Upload logo for existing entity (direct to permanent location)
   * @param {string} imageBase64 - Base64 encoded image data
   * @param {string} contentType - MIME type of the image
   * @param {string} entityType - Type of entity ('firm', 'user', 'institution')
   * @param {string} entityId - ID of the entity
   * @returns {Promise} API response with logo data
   */
  async uploadLogoForEntity(imageBase64, contentType, entityType, entityId) {
    try {
      const response = await api.put('/manage_logos', {
        image: imageBase64,
        content_type: contentType,
        entity_type: entityType,
        entity_id: entityId
      });
      
      return response.data;
    } catch (error) {
      console.error('Logo upload for entity failed:', error);
      return {
        status: 'failed',
        message: error.response?.data?.error || error.message || 'Failed to upload logo'
      };
    }
  },

  /**
   * Validate image file for upload
   * @param {File} file - File object to validate
   * @returns {Object} Validation result with isValid and error message
   */
  validateImageFile(file) {
    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }

    // Check file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      return { 
        isValid: false, 
        error: 'PNG format is preferred for best quality. JPG and SVG are also accepted.' 
      };
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return { 
        isValid: false, 
        error: 'File size must be under 5MB' 
      };
    }

    return { isValid: true };
  },

  /**
   * Convert file to base64 data URL
   * @param {File} file - File to convert
   * @returns {Promise<string>} Base64 data URL
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }
};

export default logoService; 